import React, { useState } from "react";
import api from "../../lib/api";
import { Trash2, AlertTriangle } from "lucide-react";

/**
 * Maintenance panel — destructive DB reset (testing data wipe). Two-step
 * confirm: user must type the exact WIPE_TOKEN to proceed. Preserves admin
 * accounts and config collections.
 */
const WIPE_TOKEN = "APEX_WIPE_CONFIRM";

export default function DbResetPanel() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const wipe = async () => {
    if (confirm !== WIPE_TOKEN) {
      setErr(`Zadejte přesně '${WIPE_TOKEN}' do potvrzovacího pole.`);
      return;
    }
    if (!window.confirm("OPRAVDU smazat všechna testovací data?\n\n• rezervace\n• platby\n• ne-admin uživatele\n• věrnostní slevy\n• analytiku návštěv\n\nADMIN účty + ceník + balíčky zůstanou.")) {
      return;
    }
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/db/wipe-testing-data", { confirmation: confirm });
      setResult(data);
      setConfirm("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Wipe selhal.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="db-reset-panel">
      <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2 mb-2">
        <Trash2 className="w-5 h-5 text-red-400" />
        Reset databáze · testovací data
      </h3>
      <p className="text-apex-cream/60 text-sm mb-5">Smaže veškerou testovací aktivitu a vrátí web do čistého stavu. Admin účty a konfigurace (ceník, balíčky, texty, sociální sítě) zůstávají zachovány.</p>

      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-200">
            <strong>Pozor:</strong> tato akce je nevratná. Doporučujeme spustit pouze před spuštěním webu (po finální QA), abyste nezveřejnili testovací data.
          </div>
        </div>

        <ul className="text-xs text-apex-cream/70 space-y-1 mb-4 list-disc list-inside">
          <li>Všechny rezervace (bookings)</li>
          <li>Všechny platební transakce (payment_transactions)</li>
          <li>Všichni ne-admin uživatelé</li>
          <li>Vygenerované věrnostní slevy (10% kódy)</li>
          <li>Analytika návštěv (page_views)</li>
          <li>Pokusy přihlášení (admin_login_attempts)</li>
        </ul>

        <label className="block mb-3">
          <span className="text-xs text-apex-cream/70 block mb-1">Pro potvrzení napište přesně: <code className="text-red-300 font-bold">{WIPE_TOKEN}</code></span>
          <input
            type="text"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setErr(null); }}
            placeholder={WIPE_TOKEN}
            className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-red-500/30 text-red-200 font-mono text-sm focus:outline-none focus:border-red-400"
            data-testid="db-reset-confirm-input"
          />
        </label>

        <button
          onClick={wipe}
          disabled={busy || confirm !== WIPE_TOKEN}
          className="px-4 py-2 rounded-lg bg-red-500 text-white font-semibold text-sm hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="db-reset-confirm-btn"
        >
          <Trash2 className="w-4 h-4" />
          {busy ? "Maže…" : "Definitivně smazat testovací data"}
        </button>

        {err && <div className="mt-3 text-sm text-red-300" data-testid="db-reset-err">{err}</div>}
        {result && (
          <div className="mt-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm text-emerald-200" data-testid="db-reset-result">
            <div className="font-semibold mb-1">{result.message}</div>
            <ul className="text-xs space-y-0.5 mt-2">
              {Object.entries(result.deleted || {}).map(([k, v]) => (
                <li key={k}>· {k}: <strong>{v}</strong></li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
